import Accordions from "@/common/components/Accordions";
import Checkbox from "@/common/components/Checkbox"
import MaskedTextField from "@/common/components/MaskedTextField"
import { documentsFieldSettings } from "@/common/constants/documents.constant";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { useReviewContactDocument } from "@/features/establishmentLeadsFunnel/hooks/useReviewContactDocument"
import { ContactData, ContactState } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { useIsFetching, useIsMutating, UseMutateFunction } from "@tanstack/react-query"
import { CircleAlertIcon, ThumbsDown, ThumbsUp } from "lucide-react"
import { ChangeEvent, Dispatch, SetStateAction, useState } from "react"
import { FieldErrors, UseFormRegister, UseFormWatch } from "react-hook-form"
import { EstablishmentLeadsFunnelUpdateContactValidationSchema } from "../schemas/update.schema"
import { ContactDocument } from "@/features/publicCompanyLeadsFunnel/components/ContactDocument"
import { AxiosResponse } from "axios";

interface ContactDocumentsSectionProps {
  establishmentLeadId?: string
  contact?: ContactData
  register: UseFormRegister<EstablishmentLeadsFunnelUpdateContactValidationSchema>
  watch: UseFormWatch<EstablishmentLeadsFunnelUpdateContactValidationSchema>
  errors: FieldErrors<EstablishmentLeadsFunnelUpdateContactValidationSchema>
  files: { id: string, file: Blob, fileName: string }[]
  setFiles: Dispatch<SetStateAction<{ id: string, file: Blob, fileName: string }[]>>
  showReviewButtons?: boolean
  updateLeadOnSuccess?: (response: AxiosResponse) => void
  toggleDocument: UseMutateFunction<any, Error, { value: boolean, type: string, documentId?: string }, unknown>
  editContact: boolean
  isInRegistered: boolean
  updateContactIsPending: boolean
}

export const ContactDocumentsSection = ({
  establishmentLeadId,
  contact,
  register,
  watch,
  errors,
  files,
  setFiles,
  showReviewButtons,
  updateLeadOnSuccess,
  toggleDocument,
  editContact,
  isInRegistered,
  updateContactIsPending,
}: ContactDocumentsSectionProps) => {
  const isMutating = useIsMutating();
  const isFetching = useIsFetching();

  const [documentToModal, setDocumentToModal] = useState('')

  const {
    mutate: approveContactDocument,
    isError: approveContactDocumentIsError,
  } = useReviewContactDocument({
    contactId: contact?.id,
    establishmentLeadId,
    documentId: documentToModal,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: declineContactDocument,
    isError: declineContactDocumentIsError,
  } = useReviewContactDocument({
    contactId: contact?.id,
    establishmentLeadId,
    documentId: documentToModal,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
  })

  const isServerError = (approveContactDocumentIsError || declineContactDocumentIsError)

  const docsLength = watch('contacts.documents')?.filter((doc) => editContact || doc?.checked).length;

  return (
    <div className="flex w-full flex-col gap-4">
      {(isServerError) && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <p className="text-lg font-bold">Documentos recebidos</p>
      <p className="font-bold">Pessoais</p>

      <div>
        {docsLength > 0 ? (
          <Accordions
            items={watch('contacts.documents')?.filter((doc) => editContact || doc?.checked)?.map((formDocument, index) => {
              const contactDocument = contact?.documents?.find((doc) => doc?.type === formDocument?.type);

              const declineDocument = (data?: { description?: string; }) => {
                setDocumentToModal(formDocument?.id || '');
                declineContactDocument(data);
              };

              return {
                index,
                hide: contact?.state !== ContactState.pending && !formDocument?.id,
                disabled: !formDocument?.id || !formDocument.checked,
                id: contactDocument?.id,
                dataTestid: `accordion_${formDocument?.doc_options?.name}_trigger`,
                head: (
                  <>
                    {editContact ? (
                      <Checkbox
                        label={formDocument?.doc_options?.display_name || ''}
                        name={formDocument?.doc_options?.name}
                        disabled={!editContact || updateContactIsPending || !!isMutating || !!isFetching || isInRegistered}
                        checked={formDocument?.checked || false}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          toggleDocument({
                            value: event.target.checked,
                            type: formDocument?.doc_options?.name || '',
                            documentId: formDocument?.id!,
                          })
                        }
                      />
                    ) : (
                      <div className="flex gap-[10px]">
                        {formDocument?.last_review?.state === 'approved' && (
                          <ThumbsUp className="fill-success" strokeWidth={1} />
                        )}
                        {formDocument?.last_review?.state === 'disapproved' && (
                          <ThumbsDown className="fill-warning" strokeWidth={1} />
                        )}
                        <p>{formDocument?.doc_options?.display_name}</p>
                      </div>
                    )}
                  </>
                ),
                body: (
                  <div className="flex flex-col gap-4">
                    {formDocument?.doc_options?.attributes.map((attribute) => {
                      const attributeName = Object.keys(attribute).at(0) as keyof typeof contactDocument;

                      const documentSetting =
                        attributeName in documentsFieldSettings
                          ? documentsFieldSettings[attributeName]
                          : documentsFieldSettings.default(attributeName);

                      return (
                        <MaskedTextField
                          key={`${formDocument?.doc_options?.name}.${attributeName}`}
                          label={documentSetting.label}
                          mask={documentSetting.mask}
                          markAsRequired
                          defaultValue={
                            formDocument?.[attributeName]
                              ? documentSetting.defaultValueTransformer(formDocument?.[attributeName])
                              : ''
                          }
                          {...register(`contacts.documents.${index}.${attributeName}` as const)}
                          errorMessage={(errors?.contacts?.documents?.[attributeName] as any)?.message || ' '}
                          disabled={!editContact || updateContactIsPending || !contactDocument?.id}
                        />
                      );
                    })}

                    <div>
                      <ContactDocument
                        contactDocument={contactDocument}
                        document={formDocument?.doc_options!}
                        disabled={!editContact || updateContactIsPending || !contactDocument?.id}
                        fileName={
                          files?.find((file) => file.id === contactDocument?.id)
                            ? files.find((file) => file.id === contactDocument?.id)?.fileName
                            : contactDocument?.file?.url
                              ? getFilenameFromUrl(contactDocument?.file?.url!)
                              : undefined
                        }
                        currentFileUrl={
                          files.find((file) => file.id === contactDocument?.id)
                            ? URL.createObjectURL(files.find((file) => file.id === contactDocument?.id)?.file!)
                            : contactDocument?.file?.url
                              ? contactDocument?.file?.url
                              : undefined
                        }
                        setFiles={setFiles}
                      />
                    </div>

                    {showReviewButtons && (
                      <ReviewButtons
                        reviewState={formDocument?.last_review?.state || ''}
                        onApprove={() => {
                          setDocumentToModal(formDocument?.id || '');
                          approveContactDocument({});
                        }}
                        onDecline={(data) => declineDocument(data)}
                      />
                    )}

                    {formDocument?.last_review?.state === 'disapproved' && (
                      <RequestedReview description={formDocument?.last_review.description || ''} />
                    )}
                  </div>
                ),
              };
            })}
          />
        ) : (
          <div className="flex items-center gap-2 py-4">
            <CircleAlertIcon strokeWidth={1} color="var(--warning)" />

            <div>
              <p className="font-light italic">
                Selecione os documentos na tela de edição
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
